import { useMemo, useState } from 'react';
import { ViewModeFieldset } from '@/components/ViewModeFieldset';
import { useQueryClient } from '@tanstack/react-query';
import { Lock, Pencil } from 'lucide-react';
import {
  Drawer, Field, LiveScreen, QueryState, StatusPill, formatDateTime, type Column,
} from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { useRows } from '@/hooks/useResource';
import { StatCards, applyStat } from '@/components/StatCards';
import { OrganisationProfile } from '@/screens/parts/OrganisationProfile';
import { callRpc } from '@/api/resources';
import { useAuth } from '@/auth/AuthProvider';
import { isAdmin } from '@/auth/session';
import { toast } from '@/lib/toast';

interface SettingRow {
  id: string;
  setting_key: string;
  setting_value: unknown;
  value_type: string;
  description: string | null;
  is_legal_setting: boolean;
  status: string;
  updated_at: string;
}

/**
 * Organisation settings.
 *
 * Every one of these is read by the server at the moment it matters - OTP expiry, whether an
 * approver may approve their own record, how far a mapped area may differ from the declared
 * one - so this screen edits behaviour, not preferences.
 *
 * Two of them are marked LEGAL (the EUDR forest definition and the cut-off date) and ship
 * deliberately unset. Guessing them would mean the platform made a compliance decision on the
 * owner's behalf, so they stay empty and visibly so until someone with the authority to read
 * the regulation fills them in.
 */
export default function OrgSettingsScreen() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<SettingRow | null>(null);
  // A setting opens to be read; Edit unlocks it (owner, 2026-09-27).
  const [viewing, setViewing] = useState(true);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  const settings = useRows<SettingRow>('organization_settings', { orderBy: { column: 'setting_key' } });
  const admin = isAdmin(profile);
  const [stat, setStat] = useState<string | null>(null);

  const STATS = useMemo(() => [
    { key: 'all', label: 'Settings', match: () => true, tone: 'neutral' as const },
    { key: 'unset', label: 'Not set', tone: 'warn' as const,
      match: (row: SettingRow) => renderValue(row.setting_value) === null },
    { key: 'legal', label: 'Legal', tone: 'info' as const, match: (row: SettingRow) => row.is_legal_setting },
  ], []);
  const visible = useMemo(() => applyStat(settings.data ?? [], STATS, stat), [settings.data, STATS, stat]);

  const columns: Array<Column<SettingRow>> = [
    { key: 'setting_key', label: 'Setting', render: (row) => <code>{row.setting_key}</code> },
    { key: 'description', label: 'What it does', render: (row) => row.description ?? '--' },
    {
      key: 'setting_value',
      label: 'Value',
      render: (row) => {
        const text = renderValue(row.setting_value);
        return text === null
          ? <span style={{ color: 'var(--warn)' }}>Not set</span>
          : <strong>{text}</strong>;
      },
    },
    {
      key: 'is_legal_setting', label: '',
      render: (row) => (row.is_legal_setting ? <StatusPill value="pending" /> : null),
    },
    { key: 'updated_at', label: 'Updated', render: (row) => formatDateTime(row.updated_at) },
  ];

  function open(row: SettingRow) {
    setError(undefined);
    setViewing(true);
    setEditing(row);
    setValue(renderValue(row.setting_value) ?? '');
  }

  async function save() {
    if (!editing) return;
    setError(undefined);

    // The stored JSON type is a contract: every reader casts against it, so a number written
    // as a string would not fail here - it would fail inside whichever function reads it next.
    let payload: unknown = value;
    if (editing.value_type === 'number') {
      if (value.trim() === '' || Number.isNaN(Number(value))) { setError('Enter a number.'); return; }
      payload = Number(value);
    } else if (editing.value_type === 'boolean') {
      if (!['true', 'false'].includes(value.trim())) { setError('Enter true or false.'); return; }
      payload = value.trim() === 'true';
    } else if (editing.value_type === 'json') {
      try {
        payload = JSON.parse(value);
      } catch {
        setError('That is not valid JSON.');
        return;
      }
    }

    setSaving(true);
    try {
      await callRpc('fn_set_organization_setting', {
        p_setting_key: editing.setting_key,
        p_setting_value: payload,
        p_description: editing.description,
      });
      await queryClient.invalidateQueries({ queryKey: ['organization_settings'] });
      toast.success('Saved.');
      setEditing(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <LiveScreen
      subtitle="Who this organisation is, the offices under it, and the behaviour the server reads at the moment it matters."
      title="Organisation profile"
    >
      <OrganisationProfile />

      {!admin && (
        <p className="offline-banner">
          <Lock size={13} /> You can see these settings but not change them. That needs an
          organisation admin.
        </p>
      )}

      <StatCards active={stat} onSelect={setStat} rows={settings.data ?? []} stats={STATS} />

      <QueryState empty="No settings are configured." query={settings}>
        <DataGrid exportName={'organisation-settings'} recordNoun={'setting'} searchPlaceholder={'Setting or value'} screenKey={'org.settings'} columns={columns} onRowClick={admin ? open : undefined} rows={visible} />
      </QueryState>

      <Drawer
        footer={
          <>
            {viewing ? (
              <>
                <button onClick={() => setEditing(null)} type="button">Close</button>
                <button className="primary" onClick={() => setViewing(false)} type="button"><Pencil size={14} /> Edit</button>
              </>
            ) : (
              <>
                <button className="cancel" onClick={() => setEditing(null)} type="button">Cancel</button>
                <button className="primary" disabled={saving} onClick={() => void save()} type="button">
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </>
            )}
          </>
        }
        onClose={() => setEditing(null)}
        open={Boolean(editing)}
        subtitle={editing?.description ?? undefined}
        title={editing?.setting_key ?? ''}
      >
        <ViewModeFieldset viewing={viewing}>
        {editing?.is_legal_setting && (
          <p className="login-alert">
            This is a legal setting. It shapes a compliance decision, so it ships unset on
            purpose - fill it from the regulation, not from an example.
          </p>
        )}
        <Field
          hint={editing ? `Stored as ${editing.value_type}.` : undefined}
          label="Value"
        >
          {editing?.value_type === 'json'
            ? <textarea onChange={(event) => setValue(event.target.value)} rows={5} value={value} />
            : <input onChange={(event) => setValue(event.target.value)} value={value} />}
        </Field>
        {error && <p className="login-error" role="alert">{error}</p>}
        </ViewModeFieldset>
      </Drawer>
    </LiveScreen>
  );
}

function renderValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}
